// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import * as jsonApiV2Openapi from '@digitalasset/json-api-ts-client';
import * as scanProxyOpenapi from '@digitalasset/validator-scan-proxy-api-ts-client';
import { ContractId, ContractTypeCompanion } from '@daml/types';

export default class TemplateContract<T> {
  readonly templateId: string;

  readonly contractId: ContractId<T>;

  readonly payload: T;

  readonly createdEventBlob: string;

  readonly createdAt: string;

  constructor(
    templateId: string,
    contractId: ContractId<T>,
    payload: T,
    createdEventBlob: string,
    createdAt: string,
  ) {
    this.templateId = templateId;
    this.contractId = contractId;
    this.payload = payload;
    this.createdEventBlob = createdEventBlob;
    this.createdAt = createdAt;
  }

  static fromScanProxyAPI<T extends object, K, I extends string>(
    tmpl: ContractTypeCompanion<T, K, I>,
    c: scanProxyOpenapi.Contract,
  ): TemplateContract<T> {
    return new TemplateContract(
      c.template_id,
      c.contract_id as ContractId<T>,
      tmpl.decoder.runWithException(c.payload),
      c.created_event_blob,
      c.created_at,
    );
  }

  static fromJsonAPIV2<T extends object, K, I extends string>(
    template: ContractTypeCompanion<T, K, I>,
    c: jsonApiV2Openapi.CreatedEvent,
  ): TemplateContract<T> {
    return new TemplateContract(
      c.templateId,
      c.contractId as ContractId<T>,
      template.decoder.runWithException(c.createArgument),
      // TODO: Double-check if using an empty blob is acceptable.
      c.createdEventBlob ?? '',
      c.createdAt,
    );
  }
}
